import { Grid } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { parseISO } from 'date-fns';
import { Form, Formik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PrimaryButton, TertiaryButton } from '../../Button/Button';
import { Modal } from '../../Modal/Modal';
import { InputField } from '../Input/InputField';

// Helper-function to ensure date values are Date objects
const toDate = (value: Date | string | undefined | null): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    try {
      return parseISO(value);
    } catch {
      return null;
    }
  }
  return null;
};

interface AuditLog {
  revisionNo?: number;
  revisionDate?: Date | string | null;
  internalRevisionNo?: number;
  internalRevisionDate?: Date | string | null;
}

interface AuditProps extends AuditLog {
  open: boolean;
  onChange: (audit: AuditLog) => void;
  onCancel: () => void;
}

export const AuditLog: React.FC<AuditProps> = ({
  open,
  onCancel,
  onChange,
  revisionNo,
  revisionDate,
  internalRevisionNo,
  internalRevisionDate
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      header={`${t('edit')} ${t('props.audit')}`}
      onClose={onCancel}
      open={open}
    >
      <Formik
        initialValues={
          {
            revisionNo: revisionNo,
            revisionDate: toDate(revisionDate),
            internalRevisionNo: internalRevisionNo,
            internalRevisionDate: toDate(internalRevisionDate)
          } as AuditLog
        }
        enableReinitialize
        onSubmit={values => {
          onChange(values);
          onCancel();
        }}
      >
        {({ handleChange, handleSubmit, setFieldValue, values }) => (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Form onSubmit={handleSubmit}>
              <Grid container>
                <Grid item xs={12} md={6}>
                  <InputField
                    name="revisionNo"
                    type="number"
                    label={t('props.audit')}
                    value={values.revisionNo}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    className="m-0"
                    label={t('props.auditdate')}
                    value={toDate(values.revisionDate)}
                    onChange={date => {
                      setFieldValue('revisionDate', date);
                    }}
                  />
                </Grid>
              </Grid>

              <Grid container>
                <Grid item xs={12} md={6}>
                  <InputField
                    name="internalRevisionNo"
                    type="number"
                    label={`${t('props.internal')} ${t('props.audit')}`}
                    value={values.internalRevisionNo}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    className="m-0"
                    label={`${t('props.internal')} ${t('props.auditdate')}`}
                    value={toDate(values.internalRevisionDate)}
                    onChange={date => {
                      setFieldValue('internalRevisionDate', date);
                    }}
                  />
                </Grid>
              </Grid>
              <div className="flex jc-end align-center">
                <TertiaryButton
                  onClick={onCancel}
                  label={t('cancel')}
                  className="mr-32"
                />
                <PrimaryButton type="submit" label={t('save.verb')} />
              </div>
            </Form>
          </LocalizationProvider>
        )}
      </Formik>
    </Modal>
  );
};
